import React, { createContext, useContext, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { format as dfFormat } from 'date-fns';

type DateFormatContextValue = {
  dateFormat: string;
  formatDate: (date: Date | string | number | null | undefined) => string;
  formatDateTime: (date: Date | string | number | null | undefined) => string;
};

const DateFormatContext = createContext<DateFormatContextValue | undefined>(undefined);

// Supported patterns mapping from settings format to date-fns format tokens
const mapFormat = (fmt: string): string => {
  switch (fmt) {
    case 'DD.MM.YYYY': return 'dd.MM.yyyy';
    case 'YYYY-MM-DD': return 'yyyy-MM-dd';
    case 'MM/DD/YYYY': return 'MM/dd/yyyy';
    case 'DD/MM/YYYY': return 'dd/MM/yyyy';
    default: return 'dd.MM.yyyy';
  }
};

const isValidDate = (d: any): d is Date => d instanceof Date && !isNaN(d.getTime());

export const DateFormatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: settings } = trpc.settings.getPublicSettings.useQuery(undefined, { staleTime: 5 * 60 * 1000 });
  const configured = settings?.dateFormat || 'DD.MM.YYYY';
  const dfTokens = mapFormat(configured);

  const value = useMemo<DateFormatContextValue>(() => ({
    dateFormat: configured,
    formatDate: (input) => {
      if (!input) return '-';
      const d = input instanceof Date ? input : new Date(input);
      if (!isValidDate(d)) return '-';
      try { return dfFormat(d, dfTokens); } catch { return d.toLocaleDateString(); }
    },
    formatDateTime: (input) => {
      if (!input) return '-';
      const d = input instanceof Date ? input : new Date(input);
      if (!isValidDate(d)) return '-';
      try { return dfFormat(d, dfTokens + ' HH:mm'); } catch { return d.toLocaleString(); }
    }
  }), [configured, dfTokens]);

  return <DateFormatContext.Provider value={value}>{children}</DateFormatContext.Provider>;
};

export const useDateFormat = (): DateFormatContextValue => {
  const ctx = useContext(DateFormatContext);
  if (!ctx) throw new Error('useDateFormat must be used within DateFormatProvider');
  return ctx;
};
